const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const resolveTargetIndex = (items, target) => {
  const messageId = toStringOrNull(target?.messageId ?? target?.message_id ?? target?.id ?? null);
  const serverMsgId = toStringOrNull(target?.serverMsgId ?? target?.server_msg_id ?? null);
  const clientMsgId = toStringOrNull(target?.clientMsgId ?? target?.client_msg_id ?? null);
  return items.findIndex((item) => {
    const itemId = toStringOrNull(item?.id ?? null);
    const itemServerMsgId = toStringOrNull(item?.serverMsgId ?? item?.server_msg_id ?? null);
    const itemClientMsgId = toStringOrNull(item?.clientMsgId ?? item?.client_msg_id ?? null);
    return Boolean(
      (messageId && messageId === itemId) ||
        (serverMsgId && serverMsgId === itemServerMsgId) ||
        (clientMsgId && clientMsgId === itemClientMsgId),
    );
  });
};

const PENDING_REACTION_OPERATIONS_KEY = 'pendingReactionOperations';

const resolvePendingReactionOperations = (item = null) =>
  Array.isArray(item?.[PENDING_REACTION_OPERATIONS_KEY])
    ? item[PENDING_REACTION_OPERATIONS_KEY].filter(
        (operation) => operation && typeof operation === 'object',
      )
    : [];

const resolveAuthorReactionKeys = (item = null, authorId = null) => {
  const normalizedAuthorId = toStringOrNull(authorId);
  if (!normalizedAuthorId) return [];

  return (Array.isArray(item?.reactions) ? item.reactions : [])
    .filter((reaction) =>
      (Array.isArray(reaction?.userIds) ? reaction.userIds : [])
        .map(toStringOrNull)
        .filter(Boolean)
        .includes(normalizedAuthorId),
    )
    .map(resolveMessengerV2ReactionKey)
    .filter(Boolean);
};

const removeAuthorFromReaction = (reaction = null, authorId = null, currentUserId = null) => {
  const normalizedAuthorId = toStringOrNull(authorId);
  const nextUserIds = (Array.isArray(reaction?.userIds) ? reaction.userIds : [])
    .map(toStringOrNull)
    .filter((userId) => userId && userId !== normalizedAuthorId);
  if (nextUserIds.length === 0) return null;

  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  return {
    ...reaction,
    count: nextUserIds.length,
    mine: Boolean(normalizedCurrentUserId && nextUserIds.includes(normalizedCurrentUserId)),
    userIds: nextUserIds,
  };
};

const isMatchingReactionOperation = (operation = null, candidate = null) =>
  Boolean(
    toStringOrNull(operation?.authorId) === toStringOrNull(candidate?.authorId) &&
      resolveMessengerV2ReactionKey(operation?.reaction) ===
        resolveMessengerV2ReactionKey(candidate?.reaction) &&
      toStringOrNull(operation?.reaction?.action)?.toLowerCase() ===
        toStringOrNull(candidate?.reaction?.action)?.toLowerCase(),
  );

const applyReactionToItem = (
  item,
  { reaction = null, authorId = null, currentUserId = null } = {},
) => {
  const reactionKey = resolveMessengerV2ReactionKey(reaction);
  const normalizedAuthorId = toStringOrNull(authorId);
  if (!item || !reactionKey || !normalizedAuthorId) return item;

  const currentReactions = Array.isArray(item.reactions) ? item.reactions : [];
  const shouldRemove = toStringOrNull(reaction?.action)?.toLowerCase() === 'remove';
  const reactionsWithoutAuthor = shouldRemove
    ? currentReactions
    : currentReactions
        .map((entry) => removeAuthorFromReaction(entry, normalizedAuthorId, currentUserId))
        .filter(Boolean);
  const reactionIndex = reactionsWithoutAuthor.findIndex(
    (entry) => resolveMessengerV2ReactionKey(entry) === reactionKey,
  );
  const currentReaction = reactionIndex === -1 ? null : reactionsWithoutAuthor[reactionIndex];
  const currentUserIds = Array.isArray(currentReaction?.userIds)
    ? currentReaction.userIds.map(toStringOrNull).filter(Boolean)
    : [];
  const nextUserIds = shouldRemove
    ? currentUserIds.filter((userId) => userId !== normalizedAuthorId)
    : Array.from(new Set([...currentUserIds, normalizedAuthorId]));
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  const nextReaction = {
    type: toStringOrNull(reaction?.type),
    emoji: toStringOrNull(reaction?.emoji),
    count: nextUserIds.length,
    mine: Boolean(normalizedCurrentUserId && nextUserIds.includes(normalizedCurrentUserId)),
    userIds: nextUserIds,
  };
  const nextReactions = [...reactionsWithoutAuthor];
  if (nextUserIds.length === 0) {
    if (reactionIndex !== -1) nextReactions.splice(reactionIndex, 1);
  } else if (reactionIndex === -1) {
    nextReactions.push(nextReaction);
  } else {
    nextReactions[reactionIndex] = nextReaction;
  }

  return { ...item, reactions: nextReactions };
};

export const resolveMessengerV2ReactionKey = (reaction = null) =>
  toStringOrNull(reaction?.emoji ?? reaction?.type ?? null);

export const applyMessengerV2TimelineReaction = (
  items = [],
  {
    target = null,
    reaction = null,
    authorId = null,
    currentUserId = null,
    operationId = null,
    confirmedOperationId = null,
  } = {},
) => {
  if (!Array.isArray(items)) return [];
  const targetIndex = resolveTargetIndex(items, target);
  const reactionKey = resolveMessengerV2ReactionKey(reaction);
  const normalizedAuthorId = toStringOrNull(authorId);
  if (targetIndex === -1 || !reactionKey || !normalizedAuthorId) return items;

  const normalizedOperationId = toStringOrNull(operationId);
  const targetItem = items[targetIndex];
  const operation = {
    id: normalizedOperationId,
    reaction,
    authorId: normalizedAuthorId,
    currentUserId: toStringOrNull(currentUserId),
    previousReactionKeys: resolveAuthorReactionKeys(targetItem, normalizedAuthorId),
    state: normalizedOperationId ? 'sending' : null,
    retryable: true,
  };
  const currentOperations = resolvePendingReactionOperations(targetItem);
  const normalizedConfirmedOperationId = toStringOrNull(confirmedOperationId);
  const nextOperations = normalizedOperationId
    ? [...currentOperations.filter((entry) => entry.id !== normalizedOperationId), operation]
    : currentOperations.filter(
        (entry) =>
          entry.id !== normalizedConfirmedOperationId &&
          !isMatchingReactionOperation(entry, operation),
      );
  const nextItem = applyReactionToItem(targetItem, operation);

  return items.map((item, index) =>
    index === targetIndex
      ? {
          ...nextItem,
          [PENDING_REACTION_OPERATIONS_KEY]: nextOperations,
        }
      : item,
  );
};

export const settleMessengerV2TimelineReaction = (
  items = [],
  { target = null, operationId = null, rollback = false } = {},
) => {
  if (!Array.isArray(items)) return [];
  const targetIndex = resolveTargetIndex(items, target);
  const normalizedOperationId = toStringOrNull(operationId);
  if (targetIndex === -1 || !normalizedOperationId) return items;

  const targetItem = items[targetIndex];
  const currentOperations = resolvePendingReactionOperations(targetItem);
  const operation = currentOperations.find((entry) => entry.id === normalizedOperationId);
  if (!operation) return items;

  const hasNewerPendingOperation = currentOperations.some(
    (entry) => entry.id !== normalizedOperationId && entry.authorId === operation.authorId,
  );
  let nextItem = targetItem;
  if (rollback && !hasNewerPendingOperation) {
    nextItem = {
      ...targetItem,
      reactions: (Array.isArray(targetItem.reactions) ? targetItem.reactions : [])
        .map((reaction) =>
          removeAuthorFromReaction(reaction, operation.authorId, operation.currentUserId),
        )
        .filter(Boolean),
    };
    for (const previousReactionKey of operation.previousReactionKeys || []) {
      nextItem = applyReactionToItem(nextItem, {
        ...operation,
        reaction: { emoji: previousReactionKey, action: 'add' },
      });
    }
  }
  const nextOperations = currentOperations.filter((entry) => entry.id !== normalizedOperationId);

  return items.map((item, index) =>
    index === targetIndex
      ? {
          ...nextItem,
          [PENDING_REACTION_OPERATIONS_KEY]: nextOperations,
        }
      : item,
  );
};

export const markMessengerV2TimelineReactionState = (
  items = [],
  { target = null, operationId = null, state = null, retryable = true, errorCode = null } = {},
) => {
  if (!Array.isArray(items)) return [];
  const targetIndex = resolveTargetIndex(items, target);
  const normalizedOperationId = toStringOrNull(operationId);
  const normalizedState = toStringOrNull(state);
  if (targetIndex === -1 || !normalizedOperationId || !normalizedState) return items;

  const targetItem = items[targetIndex];
  const currentOperations = resolvePendingReactionOperations(targetItem);
  if (!currentOperations.some((operation) => operation.id === normalizedOperationId)) return items;

  return items.map((item, index) =>
    index === targetIndex
      ? {
          ...item,
          [PENDING_REACTION_OPERATIONS_KEY]: currentOperations.map((operation) =>
            operation.id === normalizedOperationId
              ? {
                  ...operation,
                  state: normalizedState,
                  retryable: retryable !== false,
                  errorCode: toStringOrNull(errorCode),
                }
              : operation,
          ),
        }
      : item,
  );
};
